import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type {
    PropertyStatus,
    PropertyType,
    InvestmentType,
    Bundesland,
    RenovationCategory,
    DismissalReason,
    DealSource,
    Property,
    CostCategory,
    CostStatus,
    DocumentCategory,
    NoteType,
} from "@/types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Cent zu Euro
export function centsToEuros(cents: number | null): number | null {
  if (cents === null) return null;
  return cents / 100;
}

// Euro zu Cent
export function eurosToCents(euros: number | null): number | null {
  if (euros === null) return null;
  return Math.round(euros * 100);
}

// Formatierung: Euro
export function formatCurrency(cents: number | null): string {
  if (cents === null) return '–';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

// Formatierung: Prozent
export function formatPercent(value: number | null, decimals = 2): string {
  if (value === null) return '–';
  return `${value.toFixed(decimals)} %`;
}

// Formatierung: Datum
export function formatDate(date: string | null): string {
  if (!date) return '–';
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(date));
}

// Status Labels & Farben
export const STATUS_CONFIG: Record<
    PropertyStatus,
    { label: string; color: string }
> = {
    watchlist: {
        label: "Watchlist",
        color: "bg-purple-500/15 text-purple-400",
    },
    in_review: { label: "In Prüfung", color: "bg-amber-500/15 text-amber-400" },
    offer_submitted: {
        label: "Angebot abgegeben",
        color: "bg-blue-500/15 text-blue-400",
    },
    purchased: { label: "Gekauft", color: "bg-blue-500/15 text-blue-400" },
    in_renovation: {
        label: "In Sanierung",
        color: "bg-orange-500/15 text-orange-400",
    },
    for_sale: {
        label: "Zum Verkauf",
        color: "bg-violet-500/15 text-violet-400",
    },
    sold: { label: "Verkauft", color: "bg-green-500/15 text-green-400" },
    in_portfolio: {
        label: "Im Bestand",
        color: "bg-green-500/15 text-green-400",
    },
    dismissed: { label: "Verworfen", color: "bg-gray-500/15 text-gray-400" },
};

export function getStatusLabel(status: PropertyStatus): string {
    return STATUS_CONFIG[status]?.label || status;
}

export function getStatusColor(status: PropertyStatus): string {
    return STATUS_CONFIG[status]?.color || "bg-gray-500/15 text-gray-400";
}

// Property Type Labels
export const PROPERTY_TYPE_LABELS: Record<PropertyType, string> = {
    etw: "Eigentumswohnung",
    mfh: "Mehrfamilienhaus",
    efh: "Einfamilienhaus",
    grundstueck: "Grundstück",
    gewerbe: "Gewerbe",
    sonstige: "Sonstige",
};

export function getPropertyTypeLabel(type: PropertyType): string {
    return PROPERTY_TYPE_LABELS[type] || type;
}

// Investment Type Labels
export const INVESTMENT_TYPE_LABELS: Record<InvestmentType, string> = {
    buy_and_hold: "Buy & Hold",
    fix_and_flip: "Fix & Flip",
    self_used: "Eigennutzung",
    undecided: "Noch unklar",
};

export function getInvestmentTypeLabel(type: InvestmentType): string {
    return INVESTMENT_TYPE_LABELS[type] || type;
}

// Bundesländer mit Grunderwerbsteuer
export const BUNDESLAND_CONFIG: Record<
    Bundesland,
    { label: string; transferTaxRate: number }
> = {
    bayern: { label: "Bayern", transferTaxRate: 3.5 },
    "baden-wuerttemberg": { label: "Baden-Württemberg", transferTaxRate: 5.0 },
    berlin: { label: "Berlin", transferTaxRate: 6.0 },
    brandenburg: { label: "Brandenburg", transferTaxRate: 6.5 },
    bremen: { label: "Bremen", transferTaxRate: 5.0 },
    hamburg: { label: "Hamburg", transferTaxRate: 5.5 },
    hessen: { label: "Hessen", transferTaxRate: 6.0 },
    "mecklenburg-vorpommern": {
        label: "Mecklenburg-Vorpommern",
        transferTaxRate: 6.0,
    },
    niedersachsen: { label: "Niedersachsen", transferTaxRate: 5.0 },
    "nordrhein-westfalen": {
        label: "Nordrhein-Westfalen",
        transferTaxRate: 6.5,
    },
    "rheinland-pfalz": { label: "Rheinland-Pfalz", transferTaxRate: 5.0 },
    saarland: { label: "Saarland", transferTaxRate: 6.5 },
    sachsen: { label: "Sachsen", transferTaxRate: 5.5 },
    "sachsen-anhalt": { label: "Sachsen-Anhalt", transferTaxRate: 5.0 },
    "schleswig-holstein": { label: "Schleswig-Holstein", transferTaxRate: 6.5 },
    thueringen: { label: "Thüringen", transferTaxRate: 5.0 },
};

// Renovierungs-Kategorien
export const RENOVATION_CATEGORY_LABELS: Record<RenovationCategory, string> = {
    elektro: "Elektro",
    sanitaer: "Sanitär/Bad",
    kueche: "Küche",
    boeden: "Böden",
    maler: "Maler/Innenausbau",
    fenster: "Fenster/Türen",
    heizung: "Heizung",
    dach_fassade: "Dach/Fassade",
    sonstiges: "Sonstiges",
};

// Grunderwerbsteuer berechnen
export function calculateTransferTax(
    purchasePrice: number,
    bundesland: Bundesland,
): number {
    const rate = BUNDESLAND_CONFIG[bundesland]?.transferTaxRate || 5.0;
    return Math.round(purchasePrice * (rate / 100));
}

// Kaufnebenkosten schätzen
export function estimateAdditionalCosts(
    purchasePrice: number,
    bundesland: Bundesland,
    hasBroker: boolean = true,
): {
    transferTax: number;
    notary: number;
    landRegistry: number;
    broker: number;
    total: number;
} {
    const transferTax = calculateTransferTax(purchasePrice, bundesland);
    const notary = Math.round(purchasePrice * 0.015); // ~1.5%
    const landRegistry = Math.round(purchasePrice * 0.005); // ~0.5%
    const broker = hasBroker ? Math.round(purchasePrice * 0.0357) : 0; // ~3.57%

    return {
        transferTax,
        notary,
        landRegistry,
        broker,
        total: transferTax + notary + landRegistry + broker,
    };
}

// Dismissal Reason Labels
export const DISMISSAL_REASON_LABELS: Record<DismissalReason, string> = {
    too_expensive: "Zu teuer",
    renovation_too_complex: "Sanierung zu aufwändig",
    bad_location: "Schlechte Lage",
    financing_failed: "Finanzierung gescheitert",
    seller_backed_out: "Verkäufer abgesprungen",
    legal_issues: "Rechtliche Probleme",
    other: "Sonstiges",
};

export function getDismissalReasonLabel(reason: DismissalReason): string {
    return DISMISSAL_REASON_LABELS[reason] || reason;
}

// Deal Source Labels
export const DEAL_SOURCE_LABELS: Record<DealSource, string> = {
    immoscout: "ImmoScout24",
    immowelt: "Immowelt",
    ebay: "eBay Kleinanzeigen",
    makler: "Makler",
    off_market: "Off-Market",
    netzwerk: "Netzwerk",
    zwangsversteigerung: "Zwangsversteigerung",
    bank: "Bank",
    sonstige: "Sonstige",
};

export function getDealSourceLabel(source: DealSource): string {
    return DEAL_SOURCE_LABELS[source] || source;
}

// Fläche formatieren
export function formatArea(sqm: number | null): string {
    if (sqm === null) return "–";
    return `${sqm.toLocaleString("de-DE")} m²`;
}

// Adresse formatieren
export function formatAddress(property: Property): string {
    const parts = [
        property.address_street,
        property.address_zip && property.address_city
            ? `${property.address_zip} ${property.address_city}`
            : property.address_city,
    ].filter(Boolean);

    return parts.join(", ") || "–";
}

export const COST_CATEGORY_LABELS: Record<CostCategory, string> = {
    rohbau: "Rohbau",
    elektro: "Elektro",
    sanitaer: "Sanitär",
    heizung: "Heizung",
    boeden: "Böden",
    kueche: "Küche",
    bad: "Bad",
    maler: "Maler",
    fenster: "Fenster",
    dach: "Dach",
    fassade: "Fassade",
    aussen: "Außenanlagen",
    planung: "Planung/Architekt",
    genehmigungen: "Genehmigungen",
    sonstiges: "Sonstiges",
};

export const COST_STATUS_LABELS: Record<CostStatus, string> = {
    planned: "Geplant",
    ordered: "Beauftragt",
    paid: "Bezahlt",
};

export const DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> = {
    expose: "Exposé",
    kaufvertrag: "Kaufvertrag",
    grundbuch: "Grundbuchauszug",
    energieausweis: "Energieausweis",
    teilungserklaerung: "Teilungserklärung",
    mietvertrag: "Mietvertrag",
    rechnung: "Rechnung",
    foto_erstbesichtigung: "Foto: Erstbesichtigung",
    foto_sanierung: "Foto: Sanierung",
    foto_fertig: "Foto: Fertig",
    gutachten: "Gutachten",
    finanzierung: "Finanzierung",
    sonstiges: "Sonstiges",
};

export const NOTE_TYPE_CONFIG: Record<
    NoteType,
    { label: string; icon: string; color: string }
> = {
    note: { label: "Notiz", icon: "FileText", color: "text-foreground-muted" },
    decision: {
        label: "Entscheidung",
        icon: "CheckCircle",
        color: "text-positive",
    },
    problem: { label: "Problem", icon: "AlertCircle", color: "text-negative" },
    milestone: {
        label: "Meilenstein",
        icon: "Flag",
        color: "text-accent-gold",
    },
};